import { GHOST_AFTER_MS } from "./ghostLabel";
import type { UrgencySignal } from "./leadQuality";
import { DAY_MS } from "./window";

/**
 * TA-1313 fix round 1 (review finding 1). A stored call list row keeps each
 * why-now citation's kind, id and source (callListStore.ts), never the signal
 * it stood for. This reads the signals back from that shape, the inverse of
 * `leadQuality.ts` `urgencyFor`, which writes:
 *   - window_open: one `thread` citation on `threads.last_customer_message_at`;
 *   - window_grace: a second such citation (the final 2h, F-01);
 *   - sla_breached: one `message` citation on `messages.customer` (stage new)
 *     or `messages.reply` (a later stage);
 *   - occasion_within_30d: last, one group holding every occasion inside the
 *     horizon, each `occasion` citation followed by its supporting records.
 * A citation of any other shape reads "cited", never dropped (section 0).
 *
 * Kept in its own file, outside `leadQuality.ts` (which holds the T3 SLA
 * timers), and pinned to `assessLead`'s own output by whyNowSignals.test.ts:
 * a change to `urgencyFor` that this reading does not follow goes red there.
 * Storing the signal code on the row would make this reading unnecessary;
 * that is a schema change and is not made here.
 */
export interface WhyNowCitationShape {
  readonly kind: string;
  readonly id: string;
  readonly source: string;
}

export type WhyNowSignalCode = UrgencySignal | "cited";

export interface WhyNowSignalReading<C extends WhyNowCitationShape = WhyNowCitationShape> {
  readonly signal: WhyNowSignalCode;
  readonly citations: readonly C[];
}

const LAST_CUSTOMER = "threads.last_customer_message_at";
const SLA_SOURCES: readonly string[] = ["messages.customer", "messages.reply"];

export function urgencySignalsFromCitations<C extends WhyNowCitationShape>(whyNow: readonly C[]): WhyNowSignalReading<C>[] {
  const out: WhyNowSignalReading<C>[] = [];
  let threads = 0;
  for (let i = 0; i < whyNow.length; i++) {
    const c = whyNow[i]!;
    if (c.kind === "occasion") {
      // The occasion group is the last signal core writes: it holds every occasion and its supporting records.
      out.push({ signal: "occasion_within_30d", citations: whyNow.slice(i) });
      break;
    }
    if (c.kind === "thread" && c.source === LAST_CUSTOMER) {
      threads += 1;
      out.push({ signal: threads === 1 ? "window_open" : "window_grace", citations: [c] });
    } else if (c.kind === "message" && SLA_SOURCES.includes(c.source)) {
      out.push({ signal: "sla_breached", citations: [c] });
    } else {
      out.push({ signal: "cited", citations: [c] });
    }
  }
  return out;
}

/**
 * How a ghosted outcome came to be, in core's ratified words (item 21 of the
 * 10-03 list, quoted at `leadQuality.ts` `ghostedHow`): "no reply for 21 days,
 * auto label" or "tapped by the owner", the number being the sweep's own
 * `GHOST_AFTER_MS`, for every inquiry type, as core words it. Null for a
 * source core does not word. whyNowSignals.test.ts pins these to the stage
 * evidence `deriveStage` writes, so the two cannot drift apart.
 */
export function ghostedHowWords(source: string | undefined): string | null {
  if (source === "auto_ghost") return `no reply for ${String(Math.round(GHOST_AFTER_MS / DAY_MS))} days, auto label`;
  if (source === "owner_tap") return "tapped by the owner";
  return null;
}
